import AiStreamingApi from '@/shared/AiStreamingApi/aiStreamingApi'
import {
  AuthApi,
  BillingApi,
  AllowlistApi,
  BlocklistApi,
  CalendarGroupsApi,
  CalendarListApi,
  CalendarsApi,
  Configuration,
  EventsApi,
  FoldersApi,
  LabelsApi,
  MeApi,
  MoveApi,
  NotesApi,
  PromosApi,
  PushDevicesApi,
  TasksApi
} from '@/shared/api'
import { useNocalApi } from '@/shared/utils/api/useNocalApi'
import { registerConnectionProbe } from '@/common/composables/useOffline'
import { installWorkspaceEtagInterceptor } from '@/shared/utils/api/workspaceEtag'
import {
  MeetingCreateRequest,
  MeetingsApi,
  Configuration as ZoomConfiguration
} from '@/shared/zoom'
import globalAxios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { defineStore } from 'pinia'
import { ref, Ref } from 'vue'
import { usePreferenceStore } from './preferenceStore'

interface ApiStoreState {
  axios: AxiosInstance
  isProduction: Ref<boolean>
  isAuthenticated: Ref<boolean>
  config: Configuration
  calendars: () => CalendarsApi
  calendarList: () => CalendarListApi
  calendarGroups: () => CalendarGroupsApi
  labels: () => LabelsApi
  events: () => EventsApi
  notes: () => NotesApi
  folders: () => FoldersApi
  tasks: () => TasksApi
  blocklist: () => BlocklistApi
  allowlist: () => AllowlistApi
  workspaceState: () => {
    check: (
      ifNoneMatch?: string | null,
      options?: AxiosRequestConfig
    ) => Promise<AxiosResponse<{ revision: number; changed_at: string }>>
  }
  notesExport: () => Promise<AxiosResponse<Blob>>
  notesImport: (
    file: File,
    format: 'markdown' | 'enex',
    folderId?: string | null,
    duplicateStrategy?: 'new' | 'update' | null
  ) => Promise<AxiosResponse<unknown>>
  move: () => MoveApi
  auth: () => AuthApi
  pushDevices: () => PushDevicesApi
  aiStreaming: () => AiStreamingApi
  me: () => MeApi
  billing: () => BillingApi
  promos: () => PromosApi
  getRtEndpoint: () => string
  getApiEndpoint: () => string
  getAuthEndpoint: () => string
  initialize: (logoutHandler: () => void, forceEnv?: 'production' | 'development') => Promise<void>
  setEnvironment: (env: 'production' | 'development') => void
  setAccessToken: (token: string | null) => void
  clearCache: () => void
  testConnection: () => Promise<void>
}

interface ZoomApiState {
  config: ZoomConfiguration
  meetings: MeetingsApi
}

function setupAuthFailedInterceptor(axios: AxiosInstance, logoutHandler: () => void = () => {}) {
  axios.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      const response = error.response?.data
      if (
        response?.type === 'client_error' &&
        (response?.errors?.some((err: { code: string }) => err.code === 'authentication_failed') ||
          response?.errors?.some((err: { code: string }) => err.code === 'refresh_token_invalid'))
      ) {
        logoutHandler()
      }
      return Promise.reject(error)
    }
  )
}

function setupSpaceLimitInterceptor(axios: AxiosInstance) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 402) {
        const body = error.response?.data
        const hit =
          body?.errors?.some((err: { code: string }) => err.code === 'space_limit_reached') ||
          body?.code === 'space_limit_reached'
        if (hit) {
          import('@/common/utils/eventBus').then(({ EventBus }) => {
            EventBus.emit('space-limit-reached', {})
          })
        }
      }
      return Promise.reject(error)
    }
  )
}

export const useApi = defineStore('apiStore', (): ApiStoreState => {
  const { axios, endpoints, axiosCache, calendars, setEnvironment, ...rest } = useNocalApi(
    'production',
    5 * 60 * 1000
  )

  const logoutHandlerRef = ref(() => {})

  // The logout handlers are async (they clear Dexie/storage); this call site is
  // fire-and-forget, so catch here or a failed teardown becomes an unhandled
  // promise rejection.
  setupAuthFailedInterceptor(axios, () => {
    Promise.resolve(logoutHandlerRef.value()).catch((e) =>
      console.warn('[auth] logout after auth failure did not complete cleanly:', e)
    )
  })
  setupSpaceLimitInterceptor(axios)
  installWorkspaceEtagInterceptor(axios, { forwardToMain: true })

  const testConnection = async () => {
    // Bypass the response cache so a success actually reaches the axios
    // interceptor (a cached response is ignored there) and clears offline state.
    await calendars().getPrimaryCalendar({}, { cache: false } as AxiosRequestConfig)
  }

  // Actively recover from a stranded offline state: while the API looks offline
  // but the OS reports a connection (the cold-boot false-positive case), the
  // probe re-checks the API until a success clears the offline banner. See
  // registerConnectionProbe / useOffline for the full rationale.
  registerConnectionProbe(async () => {
    if (!rest.isAuthenticated.value) return
    await testConnection()
  })

  return {
    ...rest,
    calendars,
    axios,
    setEnvironment,
    getRtEndpoint: () => endpoints.value.rt,
    getApiEndpoint: () => endpoints.value.api,
    getAuthEndpoint: () => endpoints.value.auth,
    initialize: async (logoutHandler: () => void, forceEnv?: 'production' | 'development') => {
      logoutHandlerRef.value = logoutHandler
      if (forceEnv) {
        setEnvironment(forceEnv)
      } else if (process.env.NODE_ENV === 'production') {
        console.info('setting environment to production')
        setEnvironment('production')
      } else {
        if (!usePreferenceStore().initialized) {
          await usePreferenceStore().loadState(false)
        }
        const apiEnvironment = usePreferenceStore().localPreferences.apiEnvironment
        console.info('apiEnvironment', apiEnvironment)
        setEnvironment(apiEnvironment === 'production' ? 'production' : 'development')
      }
    },
    testConnection
  }
})

export const useZoomApi = defineStore('zoomApiStore', {
  state: (): ZoomApiState => {
    const defaultConfig = new Configuration({})
    const axios = globalAxios.create()

    return {
      config: defaultConfig,
      meetings: new MeetingsApi(defaultConfig, undefined, axios)
    }
  },
  actions: {
    async _initialize() {
      if (!this.config.accessToken) {
        this.config.accessToken = (await useApi().auth().authZoomTokenRetrieve()).data.token
      }
    },
    async createMeeting(meetingCreateRequest: MeetingCreateRequest) {
      await this._initialize()
      return this.meetings.meetingCreate({
        userId: 'me',
        meetingCreateRequest
      })
    },

    async deleteMeeting(meetingId: number) {
      await this._initialize()
      return this.meetings.meetingDelete({
        meetingId
      })
    }
  }
})
